import React, { useState, useEffect } from "react";
import { Button, DatePicker, Input, Table, Tag, Typography, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { campainAPIWithErrorHandling, campainHelpers } from "../../../api/collection/campain";
import DuplicateCampaignModal from "./DuplicateCampaignModal";
import { useSelector } from "react-redux";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmAction } from "../../../components/common/confirmDialog";
import RowActions, { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_VISIBLE_FILTERS = ["search", "date_range"];
const DEFAULT_VISIBLE_COLUMNS = ["name", "start_date", "end_date", "status", "progress", "amount_summary"];

export default function CampainTable() {
  const navigate = useNavigate();
  const [campains, setCampains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
    start_date: "",
    end_date: "",
  });

  const [dateRange, setDateRange] = useState([null, null]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const { user } = useSelector((state) => state.persistedReducer.user);
  const userType = user?.data.info.type;

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const handleDuplicate = (record) => {
    setSelectedCampaign(record);
    setDuplicateModalVisible(true);
  };

  const handleDuplicateSuccess = () => {
    setDuplicateModalVisible(false);
    setSelectedCampaign(null);
    fetchCampains(pagination.current, pagination.pageSize, searchText, filters.status);
  };

  // Fetch data using the proper API
  const fetchCampains = async (
    page = 1,
    perPage = 10,
    search = "",
    status = "",
    customDateRange = dateRange
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        status,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      if (customDateRange[0] && customDateRange[1]) {
        params.start_date = customDateRange[0].format("YYYY-MM-DD");
        params.end_date = customDateRange[1].format("YYYY-MM-DD");
      }

      const response = await campainAPIWithErrorHandling.getCampains(params);
      const { campains: campainData, pagination: paginationData } =
        campainHelpers.extractCampainList(response);

      const mappedData = campainData.map((item) => ({
        ...item,
        childRecords: item.children, // Đổi tên children
        children: undefined, // Xóa children gốc
      }));

      setCampains(mappedData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách chiến dịch");
      console.error("Error fetching campains:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    if (sorter && sorter.field) {
      const sortBy = sorter.field;
      const sortOrder = sorter.order === "descend" ? "desc" : "asc";
      setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
    }

    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0];
    }

    fetchCampains(
      paginationConfig.current,
      paginationConfig.pageSize,
      searchText,
      statusFilter
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCampains(1, pagination.pageSize, value, filters.status);
  };

  const handleReset = () => {
    setSearchText("");
    setDateRange([null, null]);
    fetchCampains(1, pagination.pageSize, "", "", [null, null]);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
    fetchCampains(1, pagination.pageSize, searchText, filters.status, dates || [null, null]);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get status tag
  const getStatusTag = (status) => {
    const statusConfig = {
      PLANNED: { color: "gray", text: "Nháp" },
      ONGOING: { color: "green", text: "Đang diễn ra" },
      CLOSED: { color: "red", text: "Đã kết thúc" },
    };
    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Action handlers
  const handleInfo = (record) => {
    navigate(`/campains/information/${record.id}`);
  };

  const handleInfoParent = (record) => {
    navigate(`/campains/detail/${record.id}`);
  };

  const handleToggleStatus = async (record, status) => {
    try {
      const action = status === "ONGOING" ? "kích hoạt" : "tạm dừng";
      const confirmed = await confirmAction({
        title: `Xác nhận ${action}`,
        content: `Bạn có chắc chắn muốn ${action} chiến dịch "${record.name}"?`,
      });
      if (!confirmed) return;

      await campainAPIWithErrorHandling.toggleCampainStatus(record.id, status);
      message.success(`${action.charAt(0).toUpperCase() + action.slice(1)} chiến dịch thành công`);

      fetchCampains(pagination.current, pagination.pageSize, searchText, filters.status);
    } catch (error) {
      message.error(error.message || "Không thể thay đổi trạng thái chiến dịch");
    }
  };

  const handleAdd = () => {
    navigate("/store-campain");
  };

  // Cột bảng con (đơn vị con của chiến dịch)
  const childColumns = [
    { title: "STT", key: "index", width: 50, align: "center", render: (_, __, index) => index + 1 },
    {
      title: "Đơn vị", key: "nameOrganization", width: 150,
      render: (_, record) => (
        <div><Text strong>{record.organization?.name}</Text></div>
      ),
    },
    {
      title: "Khách hàng", key: "paid_customers", width: 120, align: "center",
      render: (_, record) => {
        const stats = record.stats;
        return <Tag color="orange">{stats.paidCustomers}/{stats.totalCustomers}</Tag>;
      },
    },
    {
      title: "Tổng tiền", key: "amount_collected", width: 130, align: "right",
      render: (_, record) => <Tag color="orange">{formatCurrency(record.stats.paidAmount)}</Tag>,
    },
    {
      title: "Thao tác", key: "actions", width: 100, fixed: "right", align: "center",
      render: (_, record) => (
        <RowActions
          actions={[
            { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
          ]}
        />
      ),
    },
  ];

  // ===== Định nghĩa cột chính =====
  const columnDefs = [
    ...(userType === "SHOP"
      ? [{
          key: "index", label: "STT", fixed: true,
          column: { title: "STT", key: "index", width: 50, align: "center", render: (_, __, index) => index + 1 },
        }]
      : []),
    {
      key: "code", label: "Mã chiến dịch", fixed: true,
      column: {
        title: "Mã chiến dịch", dataIndex: "code", key: "code", width: 100, fixed: "left",
        render: (text) => <strong className="text-blue-600">{text}</strong>,
      },
    },
    {
      key: "name", label: "Tên chiến dịch",
      column: { title: "Tên chiến dịch", dataIndex: "name", key: "name", width: 140, sorter: true, ellipsis: true },
    },
    {
      key: "start_date", label: "Bắt đầu",
      column: {
        title: "Bắt đầu", key: "start_date", width: 110,
        render: (_, record) => <div className="text-sm">{formatDate(record.start_date)}</div>,
      },
    },
    {
      key: "end_date", label: "Kết thúc",
      column: {
        title: "Kết thúc", key: "end_date", width: 110,
        render: (_, record) => <div className="text-sm">{formatDate(record.end_date)}</div>,
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 120,
        render: (status) => getStatusTag(status),
        filters: [
          { text: "Nháp", value: "PLANNED" },
          { text: "Đang diễn ra", value: "ONGOING" },
          { text: "Đã kết thúc", value: "CLOSED" },
        ],
        filterMultiple: false,
      },
    },
    {
      key: "progress", label: "Khách hàng",
      column: {
        title: "Khách hàng", key: "progress", width: 180, align: "center",
        render: (_, record) => {
          const stats = record.stats;
          return <Tag color="blue">{stats.paidCustomers}/{stats.totalCustomers} khách</Tag>;
        },
      },
    },
    {
      key: "amount_summary", label: "Tổng tiền",
      column: {
        title: "Tổng tiền", key: "amount_summary", width: 180, align: "right",
        render: (_, record) => <Tag color="blue">{formatCurrency(record.stats.paidAmount)}</Tag>,
      },
    },
    actionColumnDef((record) => {
      const isParent = record.type_child === "PARENT";

      return [
        {
          key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view",
          onClick: () => {
            if (record.childRecords && record.childRecords.length > 0) {
              handleInfoParent(record);
            } else {
              handleInfo(record);
            }
          },
        },
        {
          key: "duplicate", label: "Sao chép", icon: <CopyOutlined />, tone: "edit",
          onClick: () => handleDuplicate(record),
        },
        {
          key: "activate", label: "Kích hoạt", icon: <PlayCircleOutlined />, tone: "success",
          hidden: !(isParent && record.status === "PLANNED"),
          onClick: () => handleToggleStatus(record, "ONGOING"),
        },
        {
          key: "pause", label: "Tạm dừng", icon: <PauseCircleOutlined />, tone: "warning",
          hidden: !(isParent && ["PLANNED", "ONGOING"].includes(record.status)),
          onClick: () => handleToggleStatus(record, "CLOSED"),
        },
      ];
    }),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm theo tên chiến dịch, mã chiến dịch..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "date_range", label: "Khoảng ngày", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <RangePicker
          style={{ width: "100%" }}
          format="DD/MM/YYYY"
          placeholder={["Từ ngày", "Đến ngày"]}
          value={dateRange}
          onChange={handleDateRangeChange}
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || (dateRange[0] && dateRange[1]));

  return (
    <BrandedPageLayout
      title="Chiến dịch"
      subtitle="Quản lý các chiến dịch thu, theo dõi tiến độ và số tiền đã thu."
      breadcrumbItems={[{ title: "Chiến dịch" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchCampains(pagination.current, pagination.pageSize, searchText, filters.status)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Thêm chiến dịch
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={campains}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có chiến dịch"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          expandedRowRender: (record) => {
            if (!record.childRecords || record.childRecords.length === 0) {
              return null;
            }
            return (
              <Table
                columns={childColumns}
                dataSource={record.childRecords}
                rowKey="id"
                pagination={false}
                size="small"
                className="bg-blue-50"
              />
            );
          },
          rowExpandable: (record) => record.childRecords && record.childRecords.length > 0,
        }}
      />

      {duplicateModalVisible && (
        <DuplicateCampaignModal
          visible={duplicateModalVisible}
          onCancel={() => {
            setDuplicateModalVisible(false);
            setSelectedCampaign(null);
          }}
          onSuccess={handleDuplicateSuccess}
          collection={selectedCampaign}
        />
      )}
    </BrandedPageLayout>
  );
}
